import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { API_BASE_URL, runBacktest, runBacktestUpstox, runTradeReplayBacktest, prepareTradeReplayBatchDataset, getInstrumentSearch } from "../api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, Cell } from "recharts";
import StatCard from "../components/StatCard";
import useMediaQuery from "../hooks/useMediaQuery";
import { downloadCsv } from "../utils/csv";
import { addDaysToYmd, formatIstDate, formatIstDateTime, previousYmdIST, todayYmdIST } from "../utils/time";

// --- Demo: synthetic candles --------------------------------------------------
const genCandles = (base = 2890, count = 300) => {
  let price = base;
  return Array.from({ length: count }, (_, i) => {
    const o = price;
    const change = (Math.random() - 0.48) * price * 0.015;
    const c = +(o + change).toFixed(2);
    const h = +(Math.max(o, c) * (1 + Math.random() * 0.005)).toFixed(2);
    const l = +(Math.min(o, c) * (1 - Math.random() * 0.005)).toFixed(2);
    price = c;
    return { open: o, high: h, low: l, close: c, volume: Math.floor(Math.random() * 500000), timestamp: Date.now() - (count - i) * 60000 };
  });
};

const DEMO_BASES = { RELIANCE: 2890, TCS: 3950, INFY: 1780, HDFCBANK: 1640, ICICIBANK: 1180 };

const INTERVALS = [
  { id: "1minute", label: "1 min (up to 28d)", maxDays: 28 },
  { id: "5minute", label: "5 min (up to 28d)", maxDays: 28 },
  { id: "15minute", label: "15 min (up to 28d)", maxDays: 28 },
  { id: "30minute", label: "30 min (up to 1y)", maxDays: 364 },
  { id: "60minute", label: "1 hour (up to 1y)", maxDays: 364 },
  { id: "day", label: "Daily (up to 1y)", maxDays: 364 },
  { id: "week", label: "Weekly (up to 10y)", maxDays: 3650 },
  { id: "month", label: "Monthly (up to 10y)", maxDays: 3650 },
];

const REPLAY_SCREENERS = [
  { id: "intraday_recovery", label: "Recovery" },
  { id: "momentum_burst", label: "Momentum" },
  { id: "bullish_crossover", label: "Crossover" },
  { id: "open_low", label: "Open=Low" },
];
const REPLAY_JSON_DEFAULT_KEY = "algobot:backtest:replay-json-default-v2";
const BACKTEST_SESSION_RESULT_KEY = "algobot:backtest:latest-result";
const DEFAULT_REPLAY_JSON = {
  name: "DeliverySweep_0940_SL10_TP18_M2_X20_P300_R1p5_bc_HL",
  fromDate: "2026-05-20",
  toDate: "2026-06-17",
  rules: {
    minEntryChangePct: 2,
    maxEntryChangePct: 20,
    minStockPrice: 300,
    minRvol: 1.5,
    screeners: ["bullish_crossover"],
    screenerMode: "any",
    requireMercuryPositive: false,
    requireMacdPositive: false,
    stopLossPct: 10,
    takeProfitPct: 18,
    trailingEnabled: false,
    trailActivationPct: 1,
    trailDistancePct: 0.5,
    exitHorizon: "date_range",
    exitModel: "range_first_hit",
    includeCosts: true,
  },
};
const replayConfigToEnv = (configBody = {}) => {
  const rules = configBody.rules || {};
  return [
    "TRADING_MODE=delivery",
    `MIN_STOCK_PRICE=${rules.minStockPrice ?? 0}`,
    "SCANNER_AUTO_TRADE_MIN_SCORE=3",
    `SCANNER_AUTO_TRADE_MIN_RVOL=${rules.minRvol ?? 0}`,
    "SCANNER_AUTO_TRADE_MIN_AVERAGE_VOLUME=500000",
    `SCANNER_AUTO_TRADE_MIN_CHANGE_PCT=${rules.minEntryChangePct ?? 0}`,
    `SCANNER_AUTO_TRADE_MAX_CHANGE_PCT=${rules.maxEntryChangePct ?? 0}`,
    `SCANNER_MAX_CHANGE_PCT=${rules.maxEntryChangePct ?? 0}`,
    `UPSTOX_INTRADAY_SCREENERS=${(rules.screeners || []).join(",")}`,
    `SCANNER_AUTO_TRADE_SCREENER_MODE=${rules.screenerMode || "any"}`,
    `SCANNER_AUTO_TRADE_REQUIRE_MERCURY_POSITIVE=${!!rules.requireMercuryPositive}`,
    `SCANNER_AUTO_TRADE_REQUIRE_MACD_POSITIVE=${!!rules.requireMacdPositive}`,
    "SCANNER_DYNAMIC_VOLUME_ENABLED=false",
    "SCANNER_MIN_LAST_CANDLE_VOLUME=10000",
    `STOP_LOSS_PCT=${rules.stopLossPct ?? 0}`,
    `STOP_LOSS_PCT_BOTH=${rules.stopLossPct ?? 0}`,
    `STOP_LOSS_PCT_EITHER=${rules.stopLossPct ?? 0}`,
    `STOP_LOSS_PCT_NEITHER=${rules.stopLossPct ?? 0}`,
    `TAKE_PROFIT_PCT=${rules.takeProfitPct ?? 0}`,
    `TAKE_PROFIT_EXTENDED_PCT=${rules.takeProfitPct ?? 0}`,
    `TRAILING_STOP_ENABLED=${!!rules.trailingEnabled}`,
    `TRAILING_STOP_ACTIVATION_PCT=${rules.trailActivationPct ?? 1}`,
    `TRAILING_STOP_DISTANCE_PCT=${rules.trailDistancePct ?? 0.5}`,
    "SIGNAL_BASED_EXITS=false",
    `BACKTEST_COSTS=${rules.includeCosts !== false}`,
  ].join("\n");
};
export default function Backtest({ botStatus }) {
  const isMobile = useMediaQuery("(max-width: 760px)");
  const [dataSource, setDataSource] = useState("replay");
  const [symbol, setSymbol] = useState("RELIANCE");
  const [instrumentKeyOverride, setInstrumentKeyOverride] = useState("");
  const [interval, setInterval] = useState("day");
  const [toDate, setToDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [symbolSearchOptions, setSymbolSearchOptions] = useState([]);
  const [showSymbolMenu, setShowSymbolMenu] = useState(false);
  const [capital, setCapital] = useState(100000);
  const [slPct, setSlPct] = useState(2);
  const [tpPct, setTpPct] = useState(4);
  const [risk, setRisk] = useState(1);
  const [strats, setStrats] = useState({ momentum: true, maCrossover: true, meanReversion: true, macd: true, mercury: true });
  const [includeCosts, setIncludeCosts] = useState(true);
  const [slippagePct, setSlippagePct] = useState(0.02);
  const [minEntryChangePct, setMinEntryChangePct] = useState(botStatus?.scannerAutoTradeMinChangePct ?? 0.3);
  const [maxEntryChangePct, setMaxEntryChangePct] = useState(botStatus?.scannerAutoTradeMaxChangePct ?? 1.25);
  const [minRvol, setMinRvol] = useState(botStatus?.scannerAutoTradeMinRvol ?? 1.5);
  const [trailEnabled, setTrailEnabled] = useState(true);
  const [trailActivationPct, setTrailActivationPct] = useState(1);
  const [trailDistancePct, setTrailDistancePct] = useState(0.75);
  const [exitHorizon, setExitHorizon] = useState("date_range");
  const [exitModel, setExitModel] = useState("range_first_hit");
  const [replayScreeners, setReplayScreeners] = useState({ intraday_recovery: false, momentum_burst: false, bullish_crossover: true, open_low: false });
  const [replayConfirmations, setReplayConfirmations] = useState({ mercury: false, macd: false });
  const [replayJsonMode, setReplayJsonMode] = useState(true);
  const [replayDefaultSaved, setReplayDefaultSaved] = useState(false);
  const [replayJsonText, setReplayJsonText] = useState(() => {
    try {
      const saved = window.localStorage.getItem(REPLAY_JSON_DEFAULT_KEY);
      if (saved) return JSON.stringify(JSON.parse(saved), null, 2);
    } catch {}
    return JSON.stringify(DEFAULT_REPLAY_JSON, null, 2);
  });
  const [result, setResult] = useState(() => {
    try {
      const saved = window.sessionStorage.getItem(BACKTEST_SESSION_RESULT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [batchJobProgress, setBatchJobProgress] = useState(null);
  const toDateRef = useRef(null);
  const fromDateRef = useRef(null);
  const symbolInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const menuRef = useRef(null);
  const batchWorkerRef = useRef(null);
  const dateDragRef = useRef(null);

  const authenticated = !!botStatus?.authenticated;
  const maxCalendarDate = botStatus?.marketClock?.previousDate || previousYmdIST();
  const replayMaxCalendarDate = todayYmdIST();
  const activeMaxCalendarDate = dataSource === "replay" ? replayMaxCalendarDate : maxCalendarDate;
  const selectedInterval = useMemo(() => INTERVALS.find((x) => x.id === interval) || INTERVALS[0], [interval]);
  const clampBacktestDate = (value) => {
    if (!value) return "";
    return value > maxCalendarDate ? maxCalendarDate : value;
  };
  const clampReplayDate = (value) => {
    if (!value) return "";
    return value > replayMaxCalendarDate ? replayMaxCalendarDate : value;
  };

  // Search for symbols from the Upstox BOD file via backend
  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 1) { setSymbolSearchOptions([]); return; }
    try {
      const data = await getInstrumentSearch(q);
      setSymbolSearchOptions(data?.matches || []);
    } catch {
      setSymbolSearchOptions([]);
    }
  }, []);

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => doSearch(symbol.trim()), 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [symbol, doSearch]);

  useEffect(() => {
    if (instrumentKeyOverride) return;
    const exact = symbolSearchOptions.find((m) => m?.symbol === symbol.trim().toUpperCase() && m.instrumentKey);
    if (exact) setInstrumentKeyOverride(exact.instrumentKey);
  }, [instrumentKeyOverride, symbol, symbolSearchOptions]);

  // Symbols always shown in dropdown: demo defaults + watchlist + search results
  const symbolChoices = useMemo(() => {
    const fromWatch = (botStatus?.watchlist || []).map((w) => w.symbol).filter(Boolean);
    return [...new Set([...Object.keys(DEMO_BASES), ...fromWatch])].sort();
  }, [botStatus?.watchlist]);

  const symbolMenuOptions = useMemo(() => {
    const map = new Map();
    // Always include local choices so dropdown has items even without API results
    symbolChoices.forEach((s) => map.set(s, { symbol: s, name: "Demo/watchlist symbol", instrumentKey: "" }));
    (botStatus?.manualWatchlist || []).forEach((m) => { if (m?.symbol) map.set(m.symbol, { symbol: m.symbol, name: "Manual watchlist", instrumentKey: m.instrumentKey || "" }); });
    // Override with API search results (have real instrumentKey)
    symbolSearchOptions.forEach((m) => { if (m?.symbol) map.set(m.symbol, m); });
    return [...map.values()].sort((a, b) => {
      // Put exact prefix matches first
      const aq = a.symbol.startsWith(symbol.toUpperCase()) ? 0 : 1;
      const bq = b.symbol.startsWith(symbol.toUpperCase()) ? 0 : 1;
      if (aq !== bq) return aq - bq;
      return a.symbol.localeCompare(b.symbol);
    }).slice(0, 60);
  }, [botStatus?.manualWatchlist, symbolChoices, symbolSearchOptions, symbol]);

  const selectSymbol = (m) => {
    setSymbol(m.symbol);
    setInstrumentKeyOverride(m.instrumentKey || "");
    setShowSymbolMenu(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          symbolInputRef.current && !symbolInputRef.current.contains(e.target)) {
        setShowSymbolMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => () => {
    if (batchWorkerRef.current) {
      batchWorkerRef.current.terminate();
      batchWorkerRef.current = null;
    }
  }, []);

  const openDatePicker = (ref) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    try { if (typeof el.showPicker === "function") el.showPicker(); } catch (_) {}
  };

  const inputStyle = {
    width: "100%", background: "#07111f", border: "1px solid #1e293b",
    color: "#e2e8f0", borderRadius: 6, padding: "8px 10px",
    fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none",
    boxSizing: "border-box",
  };

  const dateInputStyle = {
    width: "auto", minWidth: 0, background: "#0f172a", border: "1px solid #334155",
    color: "#e2e8f0", borderRadius: 6, padding: "8px 10px",
    fontSize: 13, fontFamily: "'DM Mono',monospace",
    colorScheme: "dark", boxSizing: "border-box",
  };

  const dateDragHandlers = {
    onPointerDown: (event) => {
      if (event.target?.tagName === "INPUT") return;
      const el = event.currentTarget;
      dateDragRef.current = { id: event.pointerId, x: event.clientX, left: el.scrollLeft };
      el.setPointerCapture?.(event.pointerId);
      el.style.cursor = "grabbing";
    },
    onPointerMove: (event) => {
      const drag = dateDragRef.current;
      if (!drag || drag.id !== event.pointerId) return;
      event.currentTarget.scrollLeft = drag.left - (event.clientX - drag.x);
    },
    onPointerUp: (event) => {
      dateDragRef.current = null;
      event.currentTarget.style.cursor = "grab";
    },
    onPointerCancel: (event) => {
      dateDragRef.current = null;
      event.currentTarget.style.cursor = "grab";
    },
  };

  const dateRangeRowStyle = {
    display: "flex",
    gap: 6,
    alignItems: "center",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    paddingBottom: 2,
    cursor: "grab",
  };

  const dateFieldStyle = {
    ...dateInputStyle,
    flex: isMobile ? "1 0 152px" : "1 1 0",
    minWidth: isMobile ? 152 : 0,
    maxWidth: "100%",
  };

  const dateButtonStyle = {
    background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0",
    borderRadius: 6, padding: "8px 10px", fontSize: 11,
    fontFamily: "'DM Mono',monospace", cursor: "pointer", flexShrink: 0,
  };

  const buildReplayRequestFromControls = () => ({
    fromDate,
    toDate: clampReplayDate(toDate),
    rules: {
      minEntryChangePct,
      maxEntryChangePct,
      minRvol,
      stopLossPct: slPct,
      takeProfitPct: tpPct,
      requireMercuryPositive: replayConfirmations.mercury,
      requireMacdPositive: replayConfirmations.macd,
      minStockPrice: botStatus?.minStockPrice ?? 50,
      trailingEnabled: trailEnabled,
      trailActivationPct,
      trailDistancePct,
      exitHorizon,
      exitModel,
      includeCosts,
      screeners: Object.entries(replayScreeners).filter(([, enabled]) => enabled).map(([id]) => id),
      screenerMode: "any",
    },
  });

  const loadReplayJsonFromControls = () => {
    setReplayJsonText(JSON.stringify(buildReplayRequestFromControls(), null, 2));
  };

  const formatReplayJson = () => {
    try {
      setReplayJsonText(JSON.stringify(JSON.parse(replayJsonText), null, 2));
      setError(null);
    } catch (err) {
      setError(`Replay JSON is invalid: ${err.message}`);
    }
  };

  const copyReplayJsonAsEnv = async () => {
    try {
      const parsed = JSON.parse(replayJsonText);
      if (Array.isArray(parsed) || Array.isArray(parsed?.runs)) throw new Error("Select one replay configuration first.");
      const env = replayConfigToEnv(parsed);
      await navigator.clipboard.writeText(env);
      setReplayDefaultSaved("env");
      window.setTimeout(() => setReplayDefaultSaved(false), 2000);
      setError(null);
    } catch (err) {
      setError(`Cannot copy replay JSON as env: ${err.message}`);
    }
  };

  useEffect(() => {
    try {
      if (result) window.sessionStorage.setItem(BACKTEST_SESSION_RESULT_KEY, JSON.stringify(result));
      else window.sessionStorage.removeItem(BACKTEST_SESSION_RESULT_KEY);
    } catch {}
  }, [result]);

  const saveReplayJsonDefault = () => {
    try {
      const parsed = JSON.parse(replayJsonText);
      window.localStorage.setItem(REPLAY_JSON_DEFAULT_KEY, JSON.stringify(parsed));
      setReplayJsonText(JSON.stringify(parsed, null, 2));
      setReplayDefaultSaved(true);
      window.setTimeout(() => setReplayDefaultSaved(false), 2000);
      setError(null);
    } catch (err) {
      setError(`Cannot save replay JSON default: ${err.message}`);
    }
  };

  const resetReplayJsonDefault = () => {
    try {
      window.localStorage.removeItem(REPLAY_JSON_DEFAULT_KEY);
    } catch {}
    setReplayJsonText(JSON.stringify(DEFAULT_REPLAY_JSON, null, 2));
    setReplayDefaultSaved(false);
    setError(null);
  };

  const runReplayBatchInBrowser = async (body) => {
    if (typeof Worker === "undefined") {
      throw new Error("This browser does not support Web Workers for local batch replay.");
    }

    setBatchJobProgress({ status: "preparing", totalRuns: 0, completedRuns: 0, failedRuns: 0, currentRun: "Preparing replay dataset on backend" });
    const datasetBody = Array.isArray(body)
      ? { runs: body, includeAcceptedSample: true, concurrency: 4, limit: 50 }
      : { ...body, includeAcceptedSample: true, concurrency: body?.concurrency || 4, limit: 50 };
    const dataset = await prepareTradeReplayBatchDataset(datasetBody);
    setBatchJobProgress({
      status: "running",
      totalRuns: dataset.totalRuns || 0,
      completedRuns: 0,
      failedRuns: dataset.candleErrors?.length || 0,
      currentRun: "Starting browser worker",
      candleCacheSize: dataset.candleCacheSize || 0,
      execution: "browser",
    });

    return new Promise((resolve, reject) => {
      if (batchWorkerRef.current) batchWorkerRef.current.terminate();
      const worker = new Worker(new URL("../workers/tradeReplayBatchWorker.js", import.meta.url));
      batchWorkerRef.current = worker;

      worker.onmessage = (event) => {
        const message = event.data || {};
        if (message.type === "progress") {
          setBatchJobProgress((prev) => ({
            ...(prev || {}),
            ...(message.progress || {}),
            candleCacheSize: message.progress?.candleCacheSize ?? prev?.candleCacheSize ?? dataset.candleCacheSize ?? 0,
            execution: "browser",
          }));
        }
        if (message.type === "complete") {
          worker.terminate();
          if (batchWorkerRef.current === worker) batchWorkerRef.current = null;
          setBatchJobProgress((prev) => ({
            ...(prev || {}),
            status: "completed",
            completedRuns: dataset.totalRuns || prev?.completedRuns || 0,
            totalRuns: dataset.totalRuns || prev?.totalRuns || 0,
            execution: "browser",
          }));
          resolve(message.result);
        }
        if (message.type === "error") {
          worker.terminate();
          if (batchWorkerRef.current === worker) batchWorkerRef.current = null;
          reject(new Error(message.error || "Browser batch replay failed."));
        }
      };

      worker.onerror = (err) => {
        worker.terminate();
        if (batchWorkerRef.current === worker) batchWorkerRef.current = null;
        reject(new Error(err.message || "Browser batch replay worker crashed."));
      };

      worker.postMessage({ dataset, apiBaseUrl: API_BASE_URL });
    });
  };

  const runButtonLabel = useMemo(() => {
    if (!running) {
      return ` Run ${dataSource === "replay" ? "Trade Replay" : dataSource === "demo" ? "Demo" : "Upstox"} Backtest`;
    }
    if (batchJobProgress?.status === "preparing") {
      return "Preparing replay dataset...";
    }
    if (batchJobProgress?.totalRuns) {
      const total = batchJobProgress.totalRuns;
      const completed = Math.min(batchJobProgress.completedRuns || 0, total);
      const current = batchJobProgress.status === "completed" ? total : Math.min(completed + 1, total);
      return `Running config ${current}/${total}`;
    }
    return "Running backtest...";
  }, [batchJobProgress, dataSource, running]);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    setBatchJobProgress(null);
    const options = {
      capital, stopLossPct: slPct, takeProfitPct: tpPct, riskPerTradePct: risk,
      strategies: strats,
      minEntryScore: botStatus?.minEntryScore ?? 4,
      costs: { enabled: includeCosts, slippagePct },
    };
    try {
      if (dataSource === "replay") {
        if (!authenticated) {
          setError("Connect to Upstox in Settings first. Trade replay needs Upstox candles on the server.");
          setRunning(false);
          return;
        }
        if (replayJsonMode) {
          let body;
          try {
            body = JSON.parse(replayJsonText);
          } catch (err) {
            setError(`Replay JSON is invalid: ${err.message}`);
            setRunning(false);
            return;
          }
          const isBatchReplay = Array.isArray(body) || Array.isArray(body?.runs);
          const isSingleReplay = body && typeof body === "object" && !Array.isArray(body) && (body.rules || body.fromDate || body.toDate);
          if (!isBatchReplay && !isSingleReplay) {
            setError("Replay JSON must be either a single replay object or a batch array/{ runs: [...] } object.");
            setRunning(false);
            return;
          }
          const data = isBatchReplay
            ? await runReplayBatchInBrowser(body)
            : await runTradeReplayBacktest(body);
          setResult(data);
          setRunning(false);
          return;
        }
        if (!fromDate || !toDate) {
          setError("Select a from/to date range for trade replay.");
          setRunning(false);
          return;
        }
        if (fromDate > toDate) {
          setError("From date cannot be after To date.");
          setRunning(false);
          return;
        }
        const data = await runTradeReplayBacktest(buildReplayRequestFromControls());
        setResult(data);
      } else if (dataSource === "demo") {
        const candles = genCandles(DEMO_BASES[symbol] || 2000, 300);
        const data = await runBacktest({ candles, options });
        setResult({ ...data, source: "demo", barCount: candles.length });
      } else {
        if (!authenticated) {
          setError("Connect to Upstox in Settings first. Real backtests use your access token on the server.");
          setRunning(false);
          return;
        }
        const body = {
          symbol: symbol.trim().toUpperCase(),
          instrumentKey: instrumentKeyOverride.trim() || undefined,
          interval,
          toDate: clampBacktestDate(toDate.trim()) || undefined,
          fromDate: fromDate.trim() || undefined,
          options,
        };
        if (body.fromDate && body.toDate && body.fromDate > body.toDate) {
          setError("From date cannot be after To date.");
          setRunning(false);
          return;
        }
        const data = await runBacktestUpstox(body);
        setResult(data);
      }
    } catch (e) {
      setError(e?.message || "Request failed.");
    }
    setRunning(false);
  };

  const SliderRow = ({ label, value, setter, min, max, step = 0.5, unit = "%" }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0ea5e9" }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => setter(+e.target.value)}
        style={{ width: "100%", accentColor: "#0ea5e9", height: 3 }} />
    </div>
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "280px minmax(0,1fr)", gap: 14 }}>
      <style>{`.bt-date::-webkit-calendar-picker-indicator { filter: invert(1); opacity: 0.85; cursor: pointer; }`}</style>

      {/* LEFT PANEL */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Data source */}
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 14 }}>DATA SOURCE</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[["replay", "Replay closed trades - actual entries"], ["upstox", "Real - Upstox historical API"], ["demo", "Demo - synthetic candles"]].map(([id, label]) => (
              <label key={id} style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 11, color: dataSource === id ? "#e2e8f0" : "#64748b" }}>
                <input type="radio" name="bt-src" checked={dataSource === id} onChange={() => setDataSource(id)} style={{ marginTop: 2 }} />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {dataSource !== "demo" && !authenticated && (
            <div style={{ marginTop: 10, fontSize: 10, color: "#f59e0b", lineHeight: 1.5 }}>
              Not logged in. Go to <strong>Settings -> Connect to Upstox</strong> first.
            </div>
          )}
        </div>

        {/* Config */}
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 14 }}>BACKTEST CONFIG</div>

          {/* Symbol search */}
          <div style={{ marginBottom: 14, position: "relative" }}>
            <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>SYMBOL (NSE ticker)</div>
            <input
              ref={symbolInputRef}
              value={symbol}
              onFocus={() => setShowSymbolMenu(true)}
              onChange={(e) => {
                setSymbol(e.target.value.toUpperCase());
                setInstrumentKeyOverride("");
                setShowSymbolMenu(true);
              }}
              placeholder="e.g. RELIANCE, TCS, INFY"
              style={inputStyle}
            />
            {showSymbolMenu && symbolMenuOptions.length > 0 && (
              <div
                ref={menuRef}
                style={{
                  position: "absolute", zIndex: 100, top: "100%", left: 0, right: 0,
                  marginTop: 2, maxHeight: 200, overflowY: "auto",
                  background: "#020617", border: "1px solid #1e293b", borderRadius: 8,
                  boxShadow: "0 8px 24px #00000088",
                }}
              >
                {symbolMenuOptions.map((m) => (
                  <button
                    key={`${m.symbol}-${m.instrumentKey || "local"}`}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); selectSymbol(m); }}
                    style={{
                      width: "100%", display: "block", textAlign: "left",
                      background: "transparent", border: "none",
                      borderBottom: "1px solid #0a1628", color: "#e2e8f0",
                      padding: "9px 10px", cursor: "pointer", fontSize: 12,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#0d1f35"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <strong style={{ color: "#f1f5f9" }}>{m.symbol}</strong>
                    {m.name && m.name !== "Demo/watchlist symbol" && <span style={{ color: "#64748b", marginLeft: 8, fontSize: 11 }}>{m.name}</span>}
                    {m.instrumentKey && <span style={{ color: "#334155", marginLeft: 6, fontSize: 9, fontFamily: "monospace" }}>{m.instrumentKey}</span>}
                  </button>
                ))}
              </div>
            )}
            <div style={{ fontSize: 9, color: "#334155", marginTop: 4, lineHeight: 1.4 }}>
              Type to search all NSE_EQ symbols from Upstox instrument list
            </div>
          </div>

          {dataSource === "replay" && (
            <>
              <div style={{ marginBottom: 14, padding: 10, background: "#07111f", border: "1px solid #1e293b", borderRadius: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: replayJsonMode ? "#e2e8f0" : "#64748b", marginBottom: replayJsonMode ? 10 : 0 }}>
                  <input type="checkbox" checked={replayJsonMode} onChange={(e) => setReplayJsonMode(e.target.checked)} />
                  Paste replay JSON request
                </label>
                {replayJsonMode && (
                  <>
                    <textarea
                      value={replayJsonText}
                      onChange={(e) => setReplayJsonText(e.target.value)}
                      spellCheck={false}
                      style={{
                        width: "100%",
                        minHeight: 260,
                        resize: "vertical",
                        background: "#020617",
                        border: "1px solid #334155",
                        color: "#e2e8f0",
                        borderRadius: 7,
                        padding: "10px 11px",
                        fontFamily: "'DM Mono',monospace",
                        fontSize: 11,
                        lineHeight: 1.55,
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                      <button type="button" onClick={loadReplayJsonFromControls} style={dateButtonStyle}>
                        Load from controls
                      </button>
                      <button
                        type="button"
                        onClick={formatReplayJson}
                        style={dateButtonStyle}
                      >
                        Format JSON
                      </button>
                      <button type="button" onClick={saveReplayJsonDefault} style={dateButtonStyle}>
                        {replayDefaultSaved === true ? "Default saved" : "Save default JSON"}
                      </button>
                      <button type="button" onClick={copyReplayJsonAsEnv} style={dateButtonStyle}>
                        {replayDefaultSaved === "env" ? "Env copied" : "Copy as env"}
                      </button>
                      <button type="button" onClick={resetReplayJsonDefault} style={dateButtonStyle}>
                        Reset default
                      </button>
                    </div>
                    <div style={{ fontSize: 10, color: "#64748b", marginTop: 8, lineHeight: 1.5 }}>
                      JSON mode sends single replay JSON to /backtest/trade-replay and batch JSON to /backtest/trade-replay/batch. The replay sliders below are ignored while this is enabled.
                    </div>
                  </>
                )}
              </div>
              {!replayJsonMode && (
                <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>TRADE DATE RANGE</div>
                <div {...dateDragHandlers} style={dateRangeRowStyle} title="Drag or scroll horizontally if the date fields do not fit">
                  <input ref={fromDateRef} type="date" className="bt-date" value={fromDate} max={activeMaxCalendarDate}
                    onChange={(e) => setFromDate(e.target.value)} style={dateFieldStyle} />
                  <span style={{ color: "#334155", fontSize: 11, flexShrink: 0 }}>to</span>
                  <input ref={toDateRef} type="date" className="bt-date" value={toDate} max={activeMaxCalendarDate}
                    onChange={(e) => setToDate(clampReplayDate(e.target.value))} style={dateFieldStyle} />
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  {[["1W", 7], ["2W", 14], ["1M", 30]].map(([lbl, days]) => (
                    <button key={lbl} type="button" onClick={() => {
                      const to = replayMaxCalendarDate;
                      const from = addDaysToYmd(to, -days);
                      setToDate(to);
                      setFromDate(from);
                    }} style={dateButtonStyle}>{lbl}</button>
                  ))}
                </div>
              </div>
              <SliderRow label="Min entry day move" value={minEntryChangePct} setter={setMinEntryChangePct} min={0} max={3} step={0.1} />
              <SliderRow label="Max entry day move" value={maxEntryChangePct} setter={setMaxEntryChangePct} min={0.5} max={20} step={0.25} />
              <SliderRow label="Min RVOL" value={minRvol} setter={setMinRvol} min={0} max={10} step={0.25} unit="x" />
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>EXIT HORIZON</div>
                <select value={exitHorizon} onChange={(e) => setExitHorizon(e.target.value)}
                  style={{ width: "100%", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "8px 10px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none" }}>
                  <option value="entry_day">Until entry-day close</option>
                  <option value="actual_exit">Until actual exit time</option>
                  <option value="date_range">Until selected to-date close</option>
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>EXIT MODEL</div>
                <select value={exitModel} onChange={(e) => setExitModel(e.target.value)}
                  style={{ width: "100%", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "8px 10px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none" }}>
                  <option value="range_first_hit">High/low range: first TP/SL hit wins</option>
                  <option value="range_extremes">High/low range: optimistic TP priority</option>
                  <option value="sequential">Sequential candles: first hit wins</option>
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8 }}>INTRADAY SCREENERS</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {REPLAY_SCREENERS.map((screener) => (
                    <label key={screener.id} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 11, color: replayScreeners[screener.id] ? "#e2e8f0" : "#64748b" }}>
                      <input
                        type="checkbox"
                        checked={!!replayScreeners[screener.id]}
                        onChange={() => setReplayScreeners((prev) => ({ ...prev, [screener.id]: !prev[screener.id] }))}
                      />
                      {screener.label}
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: 6, fontSize: 10, color: "#475569", lineHeight: 1.5 }}>
                  Selected screeners use OR logic, matching recorded screener tags from real trades.
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: trailEnabled ? "#e2e8f0" : "#64748b", marginBottom: 10 }}>
                <input type="checkbox" checked={trailEnabled} onChange={(e) => setTrailEnabled(e.target.checked)} />
                Enable trailing stop in replay
              </label>
              {trailEnabled && (
                <>
                  <SliderRow label="Trail activation" value={trailActivationPct} setter={setTrailActivationPct} min={0.25} max={5} step={0.25} />
                  <SliderRow label="Trail distance" value={trailDistancePct} setter={setTrailDistancePct} min={0.25} max={3} step={0.25} />
                </>
              )}
                </>
              )}
            </>
          )}

          {/* Upstox-only options */}
          {dataSource === "upstox" && (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>Instrument key (auto-filled from search, or paste)</div>
                <input value={instrumentKeyOverride} onChange={(e) => setInstrumentKeyOverride(e.target.value.trim())}
                  placeholder="e.g. NSE_EQ|INE002A01018"
                  style={{ ...inputStyle, fontSize: 10 }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>INTERVAL</div>
                <select value={interval} onChange={(e) => setInterval(e.target.value)}
                  style={{ width: "100%", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "8px 10px", fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none" }}>
                  {INTERVALS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select>
                <div style={{ fontSize: 10, color: "#64748b", lineHeight: 1.5, marginTop: 6 }}>
                  Upstox backtests use the actual returned candle range. {selectedInterval.id === "week" || selectedInterval.id === "month"
                    ? "This interval is suitable for multi-year tests."
                    : `${selectedInterval.label} is limited to about ${selectedInterval.maxDays} calendar days.`}
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>DATE RANGE (optional)</div>
                <div {...dateDragHandlers} style={dateRangeRowStyle} title="Drag or scroll horizontally if the date fields do not fit">
                  <input ref={fromDateRef} type="date" className="bt-date" value={fromDate} max={maxCalendarDate}
                    onChange={(e) => setFromDate(e.target.value)} style={dateFieldStyle} />
                  <span style={{ color: "#334155", fontSize: 11, flexShrink: 0 }}>to</span>
                  <input ref={toDateRef} type="date" className="bt-date" value={toDate} max={maxCalendarDate}
                    onChange={(e) => setToDate(clampBacktestDate(e.target.value))} style={dateFieldStyle} />
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  {[["1M", 30], ["3M", 90], ["6M", 180], ["1Y", 365]].map(([lbl, days]) => (
                    <button key={lbl} type="button" onClick={() => {
                      const to = maxCalendarDate;
                      const from = addDaysToYmd(to, -days);
                      setToDate(to);
                      setFromDate(from);
                    }} style={dateButtonStyle}>{lbl}</button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Strategy toggles */}
          {dataSource !== "replay" && <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8 }}>STRATEGIES</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {Object.entries(strats).map(([k, v]) => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: v ? "#e2e8f0" : "#475569" }}>
                  <input type="checkbox" checked={v} onChange={() => setStrats(s => ({ ...s, [k]: !s[k] }))} />
                  <span>{k === "maCrossover" ? "MA Crossover" : k === "meanReversion" ? "Mean Reversion" : k === "macd" ? "MACD" : k === "mercury" ? "Mercury CPR/EMA/VWAP" : "Momentum"}</span>
                </label>
              ))}
            </div>
          </div>}

          {dataSource === "replay" && !replayJsonMode && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8 }}>ENTRY CONFIRMATION</div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: replayConfirmations.mercury ? "#e2e8f0" : "#475569", marginBottom: 6 }}>
                <input type="checkbox" checked={replayConfirmations.mercury} onChange={() => setReplayConfirmations((s) => ({ ...s, mercury: !s.mercury }))} />
                Mercury+
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: replayConfirmations.macd ? "#e2e8f0" : "#475569" }}>
                <input type="checkbox" checked={replayConfirmations.macd} onChange={() => setReplayConfirmations((s) => ({ ...s, macd: !s.macd }))} />
                MACD+
              </label>
            </div>
          )}

          {!(dataSource === "replay" && replayJsonMode) && (
            <>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: includeCosts ? "#e2e8f0" : "#64748b" }}>
              <input type="checkbox" checked={includeCosts} onChange={(e) => setIncludeCosts(e.target.checked)} />
              Include brokerage, taxes and slippage
            </label>
          </div>

          {dataSource !== "replay" && <SliderRow label="Capital (Rs )" value={capital} setter={setCapital} min={10000} max={1000000} step={10000} unit="Rs " />}
          <SliderRow label="Stop Loss" value={slPct} setter={setSlPct} min={0.5} max={12} step={0.5} />
          <SliderRow label="Take Profit" value={tpPct} setter={setTpPct} min={1} max={20} step={0.5} />
          {dataSource !== "replay" && <SliderRow label="Risk per Trade" value={risk} setter={setRisk} min={0.5} max={5} step={0.5} />}
          <SliderRow label="Slippage" value={slippagePct} setter={setSlippagePct} min={0} max={0.2} step={0.01} />
            </>
          )}

          <button
            onClick={handleRun} disabled={running}
            style={{
              width: "100%", background: running ? "#0f172a" : "linear-gradient(135deg,#0ea5e9,#0284c7)",
              border: "none", color: running ? "#475569" : "#fff",
              padding: "12px", borderRadius: 8, cursor: running ? "not-allowed" : "pointer",
              fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 600, marginTop: 4,
            }}
          >
            {runButtonLabel}
          </button>
        </div>
      </div>

      {/* RIGHT PANEL - Results */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
        {error && (
          <div style={{ background: "#1f0a0a", border: "1px solid #7f1d1d44", borderRadius: 10, padding: 14, fontSize: 12, color: "#f87171", lineHeight: 1.6 }}>
            Warning: {error}
          </div>
        )}

        {!result && !running && !error && (
          <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 60, textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>BACKTEST</div>
            <div style={{ fontSize: 14, color: "#475569", marginBottom: 8 }}>Configure and run a backtest</div>
            <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.7 }}>
              <strong style={{ color: "#94a3b8" }}>Replay mode</strong>: re-tests your actual closed trades with new entry/exit rules.<br />
              <strong style={{ color: "#94a3b8" }}>Demo mode</strong>: uses synthetic random candles - works offline, no login needed.<br />
              <strong style={{ color: "#94a3b8" }}>Upstox mode</strong>: fetches real historical OHLC from Upstox API - requires login.
            </div>
          </div>
        )}

        {running && (
          <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 60, textAlign: "center" }}>
            {batchJobProgress ? (
              <>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>
                  {batchJobProgress.execution === "browser" ? "Browser batch replay" : "Batch replay"} {batchJobProgress.status || "running"}
                </div>
                <div style={{ fontSize: 22, color: "#e2e8f0", fontWeight: 800, marginBottom: 8 }}>
                  {batchJobProgress.completedRuns || 0}/{batchJobProgress.totalRuns || 0}
                </div>
                <div style={{ width: "100%", maxWidth: 420, height: 8, margin: "0 auto 10px", background: "#0f172a", borderRadius: 99, overflow: "hidden" }}>
                  <div
                    style={{
                      width: `${batchJobProgress.totalRuns ? Math.min(100, ((batchJobProgress.completedRuns || 0) / batchJobProgress.totalRuns) * 100) : 0}%`,
                      height: "100%",
                      background: "#0ea5e9",
                    }}
                  />
                </div>
                <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
                  Failed runs: {batchJobProgress.failedRuns || 0}
                  {batchJobProgress.currentRun ? ` | Current run: ${batchJobProgress.currentRun}` : ""}
                  {batchJobProgress.candleCacheSize ? ` | Candle sets: ${batchJobProgress.candleCacheSize}` : ""}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: "#475569" }}>Running {dataSource === "replay" ? "trade replay" : `backtest on ${symbol}`}...</div>
            )}
          </div>
        )}

        {result && <BacktestResults result={result} symbol={symbol} isMobile={isMobile} />}
      </div>
    </div>
  );
}

function BacktestResults({ result, symbol, isMobile = false }) {
  if (result?.source === "trade_replay_batch") {
    return <TradeReplayBatchResults result={result} isMobile={isMobile} />;
  }
  if (result?.source === "trade_replay") {
    return <TradeReplayResults result={result} isMobile={isMobile} />;
  }

  const equity = (result.equityCurve || []).map((point, i) => {
    if (typeof point === "number") return { i, value: point };
    return { i: point.i ?? i, value: point.capital ?? point.value ?? 0 };
  });
  const trades = result.trades || [];
  const wins = trades.filter(t => Number(t.pnl || 0) >= 0);
  const losses = trades.filter(t => Number(t.pnl || 0) < 0);
  const netPnl = result.netPnl ?? result.totalPnL ?? 0;

  return (
    <>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(4,1fr)", gap: 10 }}>
        <StatCard label="TOTAL RETURN" value={`${result.returnPct?.toFixed(2) ?? "0"}%`} color={result.returnPct >= 0 ? "#22c55e" : "#ef4444"} icon={result.returnPct >= 0 ? "+" : "-"} />
        <StatCard label="TRADES" value={trades.length} color="#0ea5e9" icon="#" />
        <StatCard label="WIN RATE" value={`${result.winRate?.toFixed(1) ?? "0"}%`} color="#f59e0b" icon="OK" />
        <StatCard label="NET P&L" value={`Rs ${netPnl.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} color={netPnl >= 0 ? "#22c55e" : "#ef4444"} icon="Rs " />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(4,1fr)", gap: 10 }}>
        <StatCard label="MAX DRAWDOWN" value={`${result.maxDrawdownPct?.toFixed(2) ?? "0"}%`} color="#ef4444" icon="-" />
        <StatCard label="PROFIT FACTOR" value={result.profitFactor?.toFixed(2) ?? "-"} color="#a78bfa" icon="x" />
        <StatCard label="AVG WIN" value={`Rs ${(result.avgWin ?? 0).toFixed(0)}`} color="#22c55e" icon="+" />
        <StatCard label="AVG LOSS" value={`Rs ${(result.avgLoss ?? 0).toFixed(0)}`} color="#ef4444" icon="-" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 10 }}>
        <StatCard label="GROSS P&L" value={`Rs ${(result.grossPnl ?? netPnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} color={(result.grossPnl ?? netPnl) >= 0 ? "#22c55e" : "#ef4444"} icon="Rs " />
        <StatCard label="COSTS" value={`Rs ${(result.totalCosts ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} color="#f59e0b" icon="-" sub="brokerage + taxes + slippage" />
        <StatCard label="COST MODE" value={result.configUsed?.costs?.enabled ? "ON" : "OFF"} color={result.configUsed?.costs?.enabled ? "#22c55e" : "#64748b"} icon="%" />
      </div>

      {/* Source info */}
      <div style={{ fontSize: 10, color: "#334155", display: "flex", gap: 16 }}>
        <span>Source: <span style={{ color: "#64748b" }}>{result.source === "demo" ? "Synthetic demo candles" : `Upstox ${result.interval || "day"} candles`}</span></span>
        {result.barCount && <span>Bars: <span style={{ color: "#64748b" }}>{result.barCount}</span></span>}
        {result.requestedFromDate && <span>Requested from: <span style={{ color: "#64748b" }}>{result.requestedFromDate}</span></span>}
        {result.fromDate && <span>From: <span style={{ color: "#64748b" }}>{result.fromDate}</span></span>}
        {result.toDate && <span>To: <span style={{ color: "#64748b" }}>{result.toDate}</span></span>}
      </div>

      {result.rangeWarning && (
        <div style={{ background: "#1f1606", border: "1px solid #92400e66", borderRadius: 10, padding: 12, fontSize: 12, color: "#fbbf24", lineHeight: 1.6 }}>
          {result.rangeWarning}
        </div>
      )}

      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 14, fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
        <strong style={{ color: "#94a3b8" }}>Trade rule:</strong> the bot enters only when composite score is at least{" "}
        <strong style={{ color: "#e2e8f0" }}>{result.configUsed?.minEntryScore ?? 4}</strong>. Momentum alone contributes +2, so a pure momentum move does not enter unless MA/RSI/MACD also confirm.
        Mercury adds CPR, EMA10/EMA21, EMA dashboard alignment, and VWAP breakout/breakdown. Losses happen when the later candle low touches stop loss or trailing stop before take profit; brokerage and taxes are deducted when cost mode is on.
      </div>

      {/* Equity curve */}
      {equity.length > 1 && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 12 }}>EQUITY CURVE</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={equity}>
              <XAxis dataKey="i" hide />
              <YAxis domain={["auto", "auto"]} tick={{ fill: "#475569", fontSize: 9 }} width={60} tickFormatter={v => `Rs ${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#020617", border: "1px solid #1e293b", borderRadius: 8, fontSize: 11 }}
                formatter={v => [`Rs ${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, "Equity"]} labelFormatter={() => ""} />
              <ReferenceLine y={result.initialCapital || 100000} stroke="#334155" strokeDasharray="4 2" />
              <Line type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Trade log */}
      {trades.length === 0 && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 18, fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
          No trades matched the selected strategy threshold for the candles actually tested.
          {result.rangeWarning
            ? " Use a higher timeframe such as Daily, Weekly, or Monthly for longer history."
            : " Try a wider date range, lower the entry threshold in backend config, or enable more confirming strategies."}
        </div>
      )}

      {trades.length > 0 && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, overflow: "auto" }}>
          <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, padding: "12px 14px", borderBottom: "1px solid #0f172a" }}>
            TRADE LOG ({trades.length})
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1120 }}>
            <thead>
              <tr style={{ background: "#07111f" }}>
                {["#", "ENTRY", "EXIT", "QTY", "GROSS", "COST", "NET P&L", "ENTRY SIGNAL", "EXIT REASON"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, color: "#475569", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.slice(-50).map((t, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #060d1a", background: i % 2 === 0 ? "#060d1a" : "#07111f" }}>
                  <td style={{ padding: "8px 12px", color: "#475569" }}>{Math.max(0, trades.length - 50) + i + 1}</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>
                    <div>Rs {Number(t.entryPrice ?? t.entry ?? 0).toFixed(2)}</div>
                    {t.entryTime && <div style={{ color: "#334155", fontSize: 9 }}>{formatIstDate(t.entryTime)}</div>}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>
                    <div>Rs {Number(t.exitPrice ?? t.exit ?? 0).toFixed(2)}</div>
                    {t.exitTime && <div style={{ color: "#334155", fontSize: 9 }}>{formatIstDate(t.exitTime)}</div>}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#64748b" }}>{t.qty || 1}</td>
                  <td style={{ padding: "8px 12px", color: (t.grossPnl ?? t.pnl ?? 0) >= 0 ? "#22c55e" : "#ef4444" }}>
                    {(t.grossPnl ?? t.pnl ?? 0) >= 0 ? "+" : ""}Rs {Number(t.grossPnl ?? t.pnl ?? 0).toFixed(2)}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#f59e0b" }}>Rs {Number(t.costs || 0).toFixed(2)}</td>
                  <td style={{ padding: "8px 12px", fontWeight: 700, color: (t.pnl || 0) >= 0 ? "#22c55e" : "#ef4444" }}>
                    {(t.pnl || 0) >= 0 ? "+" : ""}Rs {Number(t.pnl || 0).toFixed(2)}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#64748b", fontSize: 10, maxWidth: 380 }}>
                    <div style={{ color: "#e2e8f0", fontWeight: 700 }}>Score {t.entryScore ?? "?"}</div>
                    {(t.entryReasons || []).slice(0, 4).map((reason, idx) => (
                      <div key={idx}>{reason}</div>
                    ))}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#475569", fontSize: 10 }}>{t.exitReason || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function TradeReplayBatchResults({ result, isMobile = false }) {
  const resultRows = Array.isArray(result.results) ? result.results : [];
  const allRows = Array.isArray(result.allResults) ? result.allResults : resultRows;
  const rows = resultRows.slice(0, 50);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [configCopied, setConfigCopied] = useState(false);
  const [configEnvCopied, setConfigEnvCopied] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const money = (n) => `Rs ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
  const best = rows[0];
  const selectedConfigJson = selectedConfig
    ? JSON.stringify({
      name: selectedConfig.name,
      fromDate: selectedConfig.fromDate,
      toDate: selectedConfig.toDate,
      rules: selectedConfig.rules || {},
    }, null, 2)
    : "";
  const copySelectedConfig = async () => {
    if (!selectedConfigJson) return;
    try {
      await navigator.clipboard.writeText(selectedConfigJson);
      setConfigCopied(true);
      window.setTimeout(() => setConfigCopied(false), 1500);
    } catch {
      setConfigCopied(false);
    }
  };

  const copySelectedConfigAsEnv = async () => {
    if (!selectedConfig) return;
    try {
      await navigator.clipboard.writeText(replayConfigToEnv(selectedConfig));
      setConfigEnvCopied(true);
      window.setTimeout(() => setConfigEnvCopied(false), 1500);
    } catch {
      setConfigEnvCopied(false);
    }
  };

  const saveSelectedConfigDefault = () => {
    if (!selectedConfigJson) return;
    try {
      window.localStorage.setItem(REPLAY_JSON_DEFAULT_KEY, JSON.stringify(JSON.parse(selectedConfigJson)));
      setConfigSaved(true);
      window.setTimeout(() => setConfigSaved(false), 2000);
    } catch {
      setConfigSaved(false);
    }
  };
  const buildCsvRows = () => allRows.map((row, i) => {
    const rules = row.rules || {};
    const summary = row.summary || {};
    return {
      rank: i + 1,
      name: row.name || "",
      fromDate: row.fromDate || "",
      toDate: row.toDate || "",
      simulatedPnl: summary.simulatedPnl ?? "",
      totalIncludingEndWindowPnl: summary.simulatedPnl ?? "",
      actualAcceptedPnl: summary.actualAcceptedPnl ?? "",
      deltaPnl: summary.deltaPnl ?? "",
      winRate: summary.winRate ?? "",
      accepted: summary.accepted ?? "",
      rejected: summary.rejected ?? "",
      totalCandidates: summary.totalCandidates ?? "",
      errors: summary.errors ?? "",
      profitFactor: summary.profitFactor ?? "",
      avgWin: summary.avgWin ?? "",
      avgLoss: summary.avgLoss ?? "",
      bothThresholdsHit: summary.bothThresholdsHit ?? "",
      takeProfitFirst: summary.takeProfitFirst ?? "",
      stopLossFirst: summary.stopLossFirst ?? "",
      sameCandleThreshold: summary.sameCandleThreshold ?? "",
      maxHighFirst: summary.maxHighFirst ?? "",
      minLowFirst: summary.minLowFirst ?? "",
      sameCandleExtreme: summary.sameCandleExtreme ?? "",
      simulatedGrossPnl: summary.simulatedGrossPnl ?? "",
      simulatedCosts: summary.simulatedCosts ?? "",
      closedExitPnl: summary.closedExitPnl ?? "",
      closedExitCount: summary.closedExitCount ?? "",
      endWindowPnl: summary.endWindowPnl ?? "",
      endWindowCount: summary.endWindowCount ?? "",
      actualAllPnl: summary.actualAllPnl ?? "",
      rejectedActualPnl: summary.rejectedActualPnl ?? "",
      minEntryChangePct: rules.minEntryChangePct ?? "",
      maxEntryChangePct: rules.maxEntryChangePct ?? "",
      minStockPrice: rules.minStockPrice ?? "",
      minRvol: rules.minRvol ?? "",
      stopLossPct: rules.stopLossPct ?? "",
      takeProfitPct: rules.takeProfitPct ?? "",
      trailingEnabled: rules.trailingEnabled ?? "",
      trailActivationPct: rules.trailActivationPct ?? "",
      trailDistancePct: rules.trailDistancePct ?? "",
      exitHorizon: rules.exitHorizon ?? "",
      exitModel: rules.exitModel ?? "",
      screenerMode: rules.screenerMode ?? "",
      screeners: Array.isArray(rules.screeners) ? rules.screeners.join("|") : "",
      requireMercuryPositive: rules.requireMercuryPositive ?? "",
      requireMacdPositive: rules.requireMacdPositive ?? "",
      includeCosts: rules.includeCosts ?? "",
    };
  });
  const buildSampleCsvRows = () => rows.flatMap((row, rowIndex) => {
    const rules = row.rules || {};
    return (row.acceptedSample || []).map((sample, sampleIndex) => {
      const simulated = sample.simulated || {};
      const actual = sample.actual || {};
      return {
        configRank: rowIndex + 1,
        configName: row.name || "",
        sampleIndex: sampleIndex + 1,
        symbol: sample.symbol || "",
        actualPnl: actual.pnl ?? "",
        simulatedPnl: simulated.pnl ?? "",
        deltaPnl: sample.deltaPnl ?? "",
        exitReason: simulated.exitReason ?? "",
        firstThresholdHit: simulated.firstThresholdHit ?? "",
        firstThresholdTime: simulated.firstThresholdTime ?? "",
        bothThresholdsHit: simulated.bothThresholdsHit ?? "",
        firstExtreme: simulated.firstExtreme ?? "",
        firstExtremeTime: simulated.firstExtremeTime ?? "",
        entryTime: simulated.entryTime ?? "",
        entryPrice: simulated.entryPrice ?? "",
        exitPrice: simulated.exitPrice ?? "",
        target: simulated.target ?? "",
        initialStop: simulated.initialStop ?? "",
        maxHigh: simulated.maxHigh ?? "",
        maxHighTime: simulated.maxHighTime ?? "",
        minLow: simulated.minLow ?? "",
        minLowTime: simulated.minLowTime ?? "",
        targetHitTime: simulated.targetHitTime ?? "",
        stopHitTime: simulated.stopHitTime ?? "",
        candlesUsed: simulated.candlesUsed ?? "",
        stopLossPct: rules.stopLossPct ?? "",
        takeProfitPct: rules.takeProfitPct ?? "",
        requireMercuryPositive: rules.requireMercuryPositive ?? "",
        requireMacdPositive: rules.requireMacdPositive ?? "",
        exitModel: rules.exitModel ?? "",
      };
    });
  });
  const downloadAllRows = () => {
    downloadCsv("trade-replay-batch-report", buildCsvRows());
  };
  const sampleRows = buildSampleCsvRows();
  const downloadSampleRows = () => {
    downloadCsv("trade-replay-sample-diagnostics", sampleRows);
  };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(4,1fr)", gap: 10 }}>
        <StatCard label="RUNS" value={`${result.executedRuns || 0}/${result.requestedRuns || 0}`} color="#0ea5e9" icon="#" />
        <StatCard label="TOTAL INCL END WINDOW" value={money(best?.summary?.simulatedPnl)} color={(best?.summary?.simulatedPnl || 0) >= 0 ? "#22c55e" : "#ef4444"} icon="TOTAL" />
        <StatCard label="CLOSED ONLY" value={`${money(best?.summary?.closedExitPnl)} / ${best?.summary?.closedExitCount || 0}`} color={(best?.summary?.closedExitPnl || 0) >= 0 ? "#22c55e" : "#ef4444"} icon="TP" />
        <StatCard label="END WINDOW" value={`${money(best?.summary?.endWindowPnl)} / ${best?.summary?.endWindowCount || 0}`} color={(best?.summary?.endWindowPnl || 0) >= 0 ? "#22c55e" : "#ef4444"} icon="EW" />
      </div>


      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 14, fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
        <div style={{ display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: 10, flexDirection: isMobile ? "column" : "row" }}>
          <div>
            <strong style={{ color: "#94a3b8" }}>Batch replay report:</strong>{" "}
            sorted by {result.sortBy || "simulatedPnl"}. Showing {rows.length} of {allRows.length} ranked summary rows.
          </div>
          <button
            type="button"
            onClick={downloadAllRows}
            disabled={!allRows.length}
            style={{
              background: allRows.length ? "#0ea5e9" : "#1e293b",
              border: "none",
              color: "#fff",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 11,
              fontWeight: 800,
              cursor: allRows.length ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            Download displayed {allRows.length} rows CSV
          </button>
          <button
            type="button"
            onClick={downloadSampleRows}
            disabled={!sampleRows.length}
            style={{
              background: sampleRows.length ? "#334155" : "#1e293b",
              border: "none",
              color: "#fff",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 11,
              fontWeight: 800,
              cursor: sampleRows.length ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            Download {sampleRows.length} sample diagnostics
          </button>
        </div>
      </div>

      {result.errors?.length > 0 && (
        <div style={{ background: "#1f0a0a", border: "1px solid #7f1d1d44", borderRadius: 10, padding: 12, fontSize: 11, color: "#fca5a5", lineHeight: 1.6 }}>
          {result.errors.length} run error{result.errors.length === 1 ? "" : "s"}. First: {result.errors[0]?.name || `#${Number(result.errors[0]?.index || 0) + 1}`} - {result.errors[0]?.reason}
        </div>
      )}

      {selectedConfig && (
        <div style={{ background: "#060d1a", border: "1px solid #334155", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", borderBottom: "1px solid #1e293b" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>CONFIG JSON</div>
              <div style={{ fontSize: 12, color: "#e2e8f0", fontWeight: 700, overflowWrap: "anywhere" }}>{selectedConfig.name}</div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button type="button" onClick={saveSelectedConfigDefault} style={{ background: "#16a34a", border: "none", color: "#fff", borderRadius: 6, padding: "7px 10px", fontSize: 11, cursor: "pointer" }}>
                {configSaved ? "Default saved" : "Save as default"}
              </button>
              <button type="button" onClick={copySelectedConfig} style={{ background: "#0ea5e9", border: "none", color: "#fff", borderRadius: 6, padding: "7px 10px", fontSize: 11, cursor: "pointer" }}>
                {configCopied ? "Copied" : "Copy JSON"}
              </button>
              <button type="button" onClick={copySelectedConfigAsEnv} style={{ background: "#7c3aed", border: "none", color: "#fff", borderRadius: 6, padding: "7px 10px", fontSize: 11, cursor: "pointer" }}>
                {configEnvCopied ? "Env copied" : "Copy as env"}
              </button>
              <button type="button" onClick={() => { setSelectedConfig(null); setConfigCopied(false); setConfigEnvCopied(false); setConfigSaved(false); }} style={{ background: "#1e293b", border: "none", color: "#cbd5e1", borderRadius: 6, padding: "7px 10px", fontSize: 11, cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
          <pre style={{ margin: 0, padding: 12, maxHeight: 420, overflow: "auto", color: "#cbd5e1", background: "#020617", fontSize: 11, lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {selectedConfigJson}
          </pre>
        </div>
      )}

      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, overflow: "auto" }}>
        <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, padding: "12px 14px", borderBottom: "1px solid #0f172a" }}>
          CONFIG REPORT TOP {rows.length}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1620 }}>
          <thead>
            <tr style={{ background: "#07111f" }}>
              {["RANK", "NAME", "TOTAL INCL END", "CLOSED ONLY", "END WINDOW", "ACTUAL SET", "DELTA", "WIN", "ACCEPTED", "CONFIRM", "BOTH/FIRST", "SL/TP", "MOVE", "PRICE", "TRAIL", "ERRORS"].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, color: "#475569", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const rules = row.rules || {};
              const summary = row.summary || {};
              return (
                <tr key={`${row.name}-${row.index}`} style={{ borderBottom: "1px solid #060d1a", background: i % 2 === 0 ? "#060d1a" : "#07111f" }}>
                  <td style={{ padding: "8px 12px", color: "#64748b" }}>{i + 1}</td>
                  <td style={{ padding: "8px 12px", maxWidth: 220 }}>
                    <button
                      type="button"
                      onClick={() => { setSelectedConfig(row); setConfigCopied(false); setConfigEnvCopied(false); setConfigSaved(false); }}
                      title="View full replay config JSON"
                      style={{ background: "transparent", border: "none", padding: 0, color: "#38bdf8", fontWeight: 700, fontSize: 11, textAlign: "left", cursor: "pointer", overflowWrap: "anywhere" }}
                    >
                      {row.name}
                    </button>
                  </td>
                  <td style={{ padding: "8px 12px", color: summary.simulatedPnl >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{money(summary.simulatedPnl)}</td>
                  <td style={{ padding: "8px 12px", color: summary.closedExitPnl >= 0 ? "#22c55e" : "#ef4444", whiteSpace: "nowrap" }}>{money(summary.closedExitPnl)} / {summary.closedExitCount || 0}</td>
                  <td style={{ padding: "8px 12px", color: summary.endWindowPnl >= 0 ? "#22c55e" : "#ef4444", whiteSpace: "nowrap" }}>{money(summary.endWindowPnl)} / {summary.endWindowCount || 0}</td>
                  <td style={{ padding: "8px 12px", color: summary.actualAcceptedPnl >= 0 ? "#22c55e" : "#ef4444" }}>{money(summary.actualAcceptedPnl)}</td>
                  <td style={{ padding: "8px 12px", color: summary.deltaPnl >= 0 ? "#22c55e" : "#ef4444" }}>{money(summary.deltaPnl)}</td>
                  <td style={{ padding: "8px 12px", color: "#f59e0b" }}>{pct(summary.winRate)}</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{summary.accepted || 0}/{summary.totalCandidates || 0}</td>
                  <td style={{ padding: "8px 12px", color: rules.requireMercuryPositive || rules.requireMacdPositive ? "#38bdf8" : "#64748b", whiteSpace: "nowrap" }}>
                    {[
                      rules.requireMercuryPositive ? "Mercury+" : null,
                      rules.requireMacdPositive ? "MACD+" : null,
                    ].filter(Boolean).join(" + ") || "none"}
                  </td>
                  <td style={{ padding: "8px 12px", color: Number(summary.bothThresholdsHit || 0) > 0 ? "#f59e0b" : "#64748b", whiteSpace: "nowrap" }}>
                    {summary.bothThresholdsHit ?? 0} / TP {summary.takeProfitFirst ?? 0} / SL {summary.stopLossFirst ?? 0}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{rules.stopLossPct}% / {rules.takeProfitPct}%</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{rules.minEntryChangePct}% - {rules.maxEntryChangePct}%</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>>= {rules.minStockPrice}</td>
                  <td style={{ padding: "8px 12px", color: rules.trailingEnabled ? "#38bdf8" : "#64748b" }}>
                    {rules.trailingEnabled ? `${rules.trailActivationPct}% / ${rules.trailDistancePct}%` : "off"}
                  </td>
                  <td style={{ padding: "8px 12px", color: summary.errors ? "#f87171" : "#64748b" }}>{summary.errors || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TradeReplayResults({ result, isMobile = false }) {
  const summary = result.summary || {};
  const accepted = result.accepted || [];
  const rejected = result.rejected || [];
  const money = (n) => `Rs ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const screenerLabel = (id) => REPLAY_SCREENERS.find((screener) => screener.id === id)?.label || id;
  const selectedScreeners = Array.isArray(result.rules?.screeners) ? result.rules.screeners : [];
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(4,1fr)", gap: 10 }}>
        <StatCard label="TOTAL INCL END WINDOW" value={money(summary.simulatedPnl)} color={summary.simulatedPnl >= 0 ? "#22c55e" : "#ef4444"} icon="TOTAL" />
        <StatCard label="ACTUAL SAME SET" value={money(summary.actualAcceptedPnl)} color={summary.actualAcceptedPnl >= 0 ? "#22c55e" : "#ef4444"} icon="Rs " />
        <StatCard label="DELTA" value={money(summary.deltaPnl)} color={summary.deltaPnl >= 0 ? "#22c55e" : "#ef4444"} icon={summary.deltaPnl >= 0 ? "+" : "-"} />
        <StatCard label="WIN RATE" value={`${summary.winRate ?? 0}%`} color="#f59e0b" icon="%" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(4,1fr)", gap: 10 }}>
        <StatCard label="CANDIDATES" value={summary.totalCandidates || 0} color="#0ea5e9" icon="#" />
        <StatCard label="ACCEPTED" value={summary.accepted || 0} color="#22c55e" icon="OK" />
        <StatCard label="REJECTED" value={summary.rejected || 0} color="#ef4444" icon="x" />
        <StatCard label="REJECTED ACTUAL" value={money(summary.rejectedActualPnl)} color={summary.rejectedActualPnl >= 0 ? "#22c55e" : "#ef4444"} icon="Rs " />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(4,1fr)", gap: 10 }}>
        <StatCard label="CLOSED ONLY" value={`${money(summary.closedExitPnl)} / ${summary.closedExitCount || 0}`} color={summary.closedExitPnl >= 0 ? "#22c55e" : "#ef4444"} icon="TP" />
        <StatCard label="END WINDOW" value={`${money(summary.endWindowPnl)} / ${summary.endWindowCount || 0}`} color={summary.endWindowPnl >= 0 ? "#22c55e" : "#ef4444"} icon="EW" />
        <StatCard label="TP FIRST" value={summary.takeProfitFirst || 0} color="#22c55e" icon="TP" />
        <StatCard label="SL FIRST" value={summary.stopLossFirst || 0} color="#ef4444" icon="SL" />
      </div>

      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 14, fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
        <strong style={{ color: "#94a3b8" }}>Replay rules:</strong>{" "}
        entry move {result.rules?.minEntryChangePct}% to {result.rules?.maxEntryChangePct}%, RVOL >= {result.rules?.minRvol}x,
        screeners {selectedScreeners.length ? selectedScreeners.map(screenerLabel).join(" / ") : "off"},
        SL {result.rules?.stopLossPct}%, TP {result.rules?.takeProfitPct}%, trailing {result.rules?.trailingEnabled ? "on" : "off"},
        horizon {result.rules?.exitHorizon}, model {result.rules?.exitModel || "sequential"}. From {result.fromDate || "start"} to {result.toDate}.
      </div>

      {accepted.length > 0 && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, overflow: "auto" }}>
          <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, padding: "12px 14px", borderBottom: "1px solid #0f172a" }}>
            ACCEPTED REPLAY TRADES ({accepted.length})
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1240 }}>
            <thead>
              <tr style={{ background: "#07111f" }}>
                {["SYMBOL", "TRADE DATE", "ENTRY", "ACTUAL EXIT", "SIM EXIT", "GAIN/LOSS %", "ACTUAL P&L", "SIM P&L", "DELTA", "SIM REASON", "ENTRY FILTERS"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, color: "#475569", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accepted.map((row, i) => (
                <tr key={`${row.symbol}-${i}`} style={{ borderBottom: "1px solid #060d1a", background: i % 2 === 0 ? "#060d1a" : "#07111f" }}>
                  <td style={{ padding: "8px 12px", color: "#e2e8f0", fontWeight: 700 }}>{row.symbol}</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>
                    <div>{row.actual.entryTime ? formatIstDateTime(row.actual.entryTime) : "-"}</div>
                    {row.actual.exitTime && <div style={{ color: "#334155", fontSize: 9 }}>Exit {formatIstDateTime(row.actual.exitTime)}</div>}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>Rs {Number(row.actual.entryPrice || 0).toFixed(2)}</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>Rs {Number(row.actual.exitPrice || 0).toFixed(2)}</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>Rs {Number(row.simulated.exitPrice || 0).toFixed(2)}</td>
                  <td style={{ padding: "8px 12px", color: Number(row.simulated.tradeReturnPct || 0) >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
                    {Number(row.simulated.tradeReturnPct || 0) >= 0 ? "+" : ""}{Number(row.simulated.tradeReturnPct || 0).toFixed(2)}%
                  </td>
                  <td style={{ padding: "8px 12px", color: row.actual.pnl >= 0 ? "#22c55e" : "#ef4444" }}>{money(row.actual.pnl)}</td>
                  <td style={{ padding: "8px 12px", color: row.simulated.pnl >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{money(row.simulated.pnl)}</td>
                  <td style={{ padding: "8px 12px", color: row.deltaPnl >= 0 ? "#22c55e" : "#ef4444" }}>{money(row.deltaPnl)}</td>
                  <td style={{ padding: "8px 12px", color: "#64748b" }}>{row.simulated.exitReason}</td>
                  <td style={{ padding: "8px 12px", color: "#64748b" }}>
                    Move {row.entry.entryChangePct ?? "-"}% / RVOL {row.entry.rvol ?? "-"}x
                    {row.entry?.screeners?.length ? ` / ${row.entry.screeners.map(screenerLabel).join(", ")}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejected.length > 0 && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, overflow: "auto" }}>
          <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, padding: "12px 14px", borderBottom: "1px solid #0f172a" }}>
            REJECTED CANDIDATES ({rejected.length})
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 920 }}>
            <thead>
              <tr style={{ background: "#07111f" }}>
                {["SYMBOL", "TRADE DATE", "ACTUAL P&L", "ENTRY MOVE", "RVOL", "REASON"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, color: "#475569", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rejected.slice(0, 80).map((row, i) => (
                <tr key={`${row.symbol}-${i}`} style={{ borderBottom: "1px solid #060d1a", background: i % 2 === 0 ? "#060d1a" : "#07111f" }}>
                  <td style={{ padding: "8px 12px", color: "#e2e8f0", fontWeight: 700 }}>{row.symbol}</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>
                    <div>{row.actual?.entryTime ? formatIstDateTime(row.actual.entryTime) : "-"}</div>
                    {row.actual?.exitTime && <div style={{ color: "#334155", fontSize: 9 }}>Exit {formatIstDateTime(row.actual.exitTime)}</div>}
                  </td>
                  <td style={{ padding: "8px 12px", color: row.actual?.pnl >= 0 ? "#22c55e" : "#ef4444" }}>{money(row.actual?.pnl)}</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{row.entry?.entryChangePct ?? "-"}%</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{row.entry?.rvol ?? "-"}x</td>
                  <td style={{ padding: "8px 12px", color: "#64748b" }}>
                    {row.rejectedReason}
                    {row.entry?.screeners?.length ? <div style={{ color: "#334155", fontSize: 9 }}>{row.entry.screeners.map(screenerLabel).join(", ")}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
